/*
 * CEOChallengeLeaderboard — Public leaderboard for the CEO Challenge.
 * Read-only; masks names when the config says so.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Trophy } from "lucide-react";
import { LemonBot, THEME } from "./ceo_ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CEOChallengeLeaderboard() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/ceo-challenge/leaderboard?limit=50`);
        if (!alive) return;
        setRows(res.data.leaderboard || []);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load leaderboard.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className={`${THEME.bg} text-white min-h-screen`} data-testid="ceo-leaderboard-root">
      <div className="max-w-3xl mx-auto px-4 py-10 sm:py-14">
        <button
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-bold mb-6"
          data-testid="ceo-leaderboard-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex items-center gap-4 mb-8">
          <LemonBot size={80} mood="cheer" />
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-[#FFD500]">Youth CEO Camp</p>
            <h1 className="text-3xl sm:text-4xl font-black" data-testid="ceo-leaderboard-title">
              <Trophy className="inline w-8 h-8 mr-2 text-[#FFD500]" />
              Challenge Leaderboard
            </h1>
          </div>
        </div>

        {loading && <p className="text-white/60">Loading…</p>}
        {error && <p className="text-[#FCA5A5]">{error}</p>}

        {!loading && !error && (
          <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 divide-y divide-white/10 overflow-hidden">
            {rows.length === 0 && (
              <p className="p-6 text-white/60">No completed attempts yet — be the first!</p>
            )}
            {rows.map((row) => (
              <div key={row.user_id} className="flex items-center gap-4 p-4 sm:p-5" data-testid={`ceo-leaderboard-row-${row.rank}`}>
                <span
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-lg shrink-0 ${
                    row.rank === 1
                      ? "bg-[#FFD500] text-black"
                      : row.rank === 2
                      ? "bg-white/80 text-black"
                      : row.rank === 3
                      ? "bg-[#F59E0B] text-black"
                      : "bg-white/10 text-white"
                  }`}
                >
                  {row.rank}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-black text-base sm:text-lg truncate">{row.name}</p>
                  {row.team_name && (
                    <p className="text-xs text-white/50 truncate">{row.team_name}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="font-black text-lg">{row.score}</p>
                  <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold">
                    {row.correct_count}/20 correct
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
